import "./Banner.scss";
import React, { useEffect, useRef, useState } from "react";
import { BannerData, getBannerData } from "@me8eon/devmode";
import useSearchStore from "../../store/searchStore";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { useLanguage } from "@me8eon/language";
import { aiConfig } from "../..";
import { useJwtToken } from "@me8eon/react_authentication";
import { EonUiIcon } from "@eon-ui/eon-ui-components-react";

const isBannerInTimeRange = (banner: BannerData, nowMs: number): boolean => {
    const start = banner.start ? new Date(banner.start ?? "") : null;
    const end = banner.end ? new Date(banner.end ?? "") : null;

    if (banner.start && banner.end) {
        if (start && end) {
            return nowMs > start.getTime() && nowMs < end.getTime();
        } else if (start) {
            return nowMs > start.getTime();
        } else if (end) {
            return nowMs < end.getTime();
        }
    } else if (banner.start && start) {
        return nowMs > start.getTime();
    } else if (banner.end && end) {
        return nowMs < end.getTime();
    }
    return true;
};

export const Banner: React.FC = () => {
    const { jwtTokenFn } = aiConfig;
    const [list, setList] = useState<BannerData[]>([]);
    const [show, setShow] = useState<BannerData[]>([]);
    const [language] = useLanguage();
    const { favData, updateBannerData, setPrefData } = useSearchStore();
    const marqueeTrackRef = useRef<HTMLDivElement | null>(null);
    const marqueeViewportRef = useRef<HTMLDivElement | null>(null);
    const marqueeUnitRef = useRef<HTMLDivElement | null>(null);
    const [marqueeDuration, setMarqueeDuration] = useState<number>(30);
    const [repeatCount, setRepeatCount] = useState<number>(2);
    const jwt = useJwtToken();

    const getBannerIcon = (iconName?: string) => {
        const styles = { height: 20, width: 20, flexShrink: 0 };
        switch (iconName) {
        case "alert":
            return <EonUiIcon name="alert" style={styles} scheme="red500" />;
        case "info":
        default:
            return <EonUiIcon name="info" style={styles} />;
        }
    };

    const dismissBanner = (bannerId: string) => {
        const date = new Date().toLocaleDateString();
        let banner = favData.banner ?? {};
        let listKeys = list.map((x) => x.id);
        let updatedBanner: Record<string, string> = {};
        Object.keys(banner).forEach((key) => {
            if (listKeys.includes(key)) {
                updatedBanner[key] = banner[key];
            }
        });
        updateBannerData({ ...updatedBanner, [bannerId]: date });
        setPrefData(jwt);
    };

    const dismissAllBanners = () => {
        const date = new Date().toLocaleDateString();
        const bannerIds = show.map((banner) => banner.id);
        const updateData: Record<string, string> = {};
        bannerIds.forEach((id) => {
            updateData[id] = date;
        });
        let banner = favData.banner ?? {};
        let listKeys = list.map((x) => x.id);
        let updatedBanner: Record<string, string> = {};
        Object.keys(banner).forEach((key) => {
            if (listKeys.includes(key)) {
                updatedBanner[key] = banner[key];
            }
        });
        updateBannerData({ ...updatedBanner, ...updateData });
        setPrefData(jwt);
    };

    useEffect(() => {
        getBannerData(jwtTokenFn)
            .then((r) => { setList(r); })
            .catch((err) => console.log("Failed to fetch banner data:", err));
    }, []);

    useEffect(() => {
        if (list.length > 0) {
            const banner = favData.banner ?? {};
            const today = new Date();
            const last7Days = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(today);
                d.setDate(d.getDate() - i);
                return d.toLocaleDateString();
            });
            const date = today.toLocaleDateString();
            const nowMs = Date.now();
            const hiddenBanners = Object.keys(banner);
            const isHidden = (id: string, reoccurrence: BannerData["reoccurrence"]): boolean => {
                if (hiddenBanners.includes(id)) {
                    if (!reoccurrence || reoccurrence === "none") {
                        return Boolean(banner[id]); 
                    } else if (reoccurrence === "daily") {
                        return banner[id] === date;
                    } else if (reoccurrence === "weekly") {                   
                        return last7Days.includes(banner[id]);
                    } 
                }
                return false;
            };
            const activeList = list.filter(
                (x) => x.active && !isHidden(x.id, x.reoccurrence) && isBannerInTimeRange(x, nowMs)
            );
            const allBlanks = activeList.every((x) => !x.content?.trim() || !(x[`content_${language}` as keyof BannerData] as string)?.trim());
            setShow(allBlanks ? [] : activeList);
            // setShow(activeList); // Show all active banners that are not hidden one below other
            // setShow(activeList.length > 0 ? [activeList[0]] : []); // Show only the first active banner that is not hidden
        } else {
            setShow([]);
        }
    }, [favData, list, language]);

    const currentBanner = show[0];

    const renderedMarkdownById = React.useMemo(() => {
        const contentMap: Record<string, string> = {};
        show.forEach((item) => {
            const content: string = language
                ? ((item[
                    `content_${language}` as keyof BannerData
                ] as string) ?? item.content ?? "")
                : (item.content ?? "");
            const markdownHtml = marked.parse(content, {
                async: false,
            }) as string;
            contentMap[item.id] = DOMPurify.sanitize(markdownHtml, {
                ADD_ATTR: ["target", "rel"],
            });
        });
        return contentMap;
    }, [show, language]);

    useEffect(() => {
        if (show.length <= 1) {
            return;
        }

        const pxPerSecond = 60;
        const recalculateMarquee = () => {
            if (
                !marqueeTrackRef.current ||
                !marqueeViewportRef.current ||
                !marqueeUnitRef.current
            ) {
                return;
            }

            const bannerElement = document.getElementById("banner");
            // const marqueeItems = document.querySelectorAll(
            //     "#banner .marquee-item"
            // );
            // if (bannerElement && marqueeItems.length > 0) {
            //     const itemMinWidth = Math.max(bannerElement.clientWidth - 64, 0);
            //     marqueeItems.forEach((item) => {
            //         (item as HTMLElement).style.minWidth = `${itemMinWidth}px`;
            //     });
            // }

            const marqueeGroups = document.querySelectorAll(
                "#banner .marquee-group"
            );
            if (bannerElement && marqueeGroups.length > 0) {
                const itemMinWidth = Math.max(bannerElement.clientWidth - 64, 0);
                marqueeGroups.forEach((item) => {
                    (item as HTMLElement).style.minWidth = `${itemMinWidth}px`;
                });
            }

            const viewportWidth = marqueeViewportRef.current.clientWidth;
            const singleSetWidth = marqueeUnitRef.current.scrollWidth;
            if (!viewportWidth || !singleSetWidth) {
                return;
            }

            // Repeat enough times to cover the viewport, with a cap to avoid excessive DOM nodes.
            const nextRepeatCount = Math.max(
                2,
                // Math.min(5, Math.ceil(viewportWidth / singleSetWidth) + 1)
            );
            const loopDistance = singleSetWidth * nextRepeatCount;
            const duration = Math.max(loopDistance / pxPerSecond, 8);

            setRepeatCount((old) =>
                old === nextRepeatCount ? old : nextRepeatCount
            );
            setMarqueeDuration((old) =>
                Math.abs(old - duration) < 0.1 ? old : duration
            );
        };

        recalculateMarquee();
        requestAnimationFrame(recalculateMarquee);
        window.addEventListener("resize", recalculateMarquee);

        return () => {
            window.removeEventListener("resize", recalculateMarquee);
        };
    }, [show, renderedMarkdownById, language]);

    if (!show || show.length === 0) {
        return null;
    }

    if (show.length === 1) {
        return (
            <div className="banner" key={currentBanner.id} id="banner">
                <div className="banner-content">
                    {currentBanner.icon && (
                        <div className="alert">
                            {getBannerIcon(currentBanner.icon)}
                        </div>
                    )}
                    <div
                        className="markdown-content markdown-content-wrap"
                        dangerouslySetInnerHTML={{
                            __html: renderedMarkdownById[currentBanner.id],
                        }}
                    />
                </div>
                <button
                    type="button"
                    className="icon"
                    aria-label="Dismiss banner"
                    onClick={() => dismissBanner(currentBanner.id)}
                >
                    <EonUiIcon
                        name="close"
                        style={{ height: 12, width: 12, flexShrink: 0 }}
                    />
                </button>
            </div>
        );
    }

    return (
        <div className="banner marquee-container" id="banner">
            <div className="marquee-viewport" ref={marqueeViewportRef}>
                <div
                    className="marquee-measure"
                    ref={marqueeUnitRef}
                    aria-hidden
                >
                    {show.map((item) => (
                        <div
                            className="marquee-item"
                            key={`measure-${item.id}`}
                        >
                            {item.icon && (
                                <div className="alert">
                                    {getBannerIcon(item.icon)}
                                </div>
                            )}
                            <div
                                className="markdown-content"
                                dangerouslySetInnerHTML={{
                                    __html: renderedMarkdownById[item.id],
                                }}
                            />
                        </div>
                    ))}
                </div>
                <div
                    className="marquee-track"
                    ref={marqueeTrackRef}
                    style={{ animationDuration: `${marqueeDuration}s` }}
                >
                    {Array.from({ length: repeatCount }).map(
                        (_, repeatIndex) => (
                            <div className="marquee-group" key={`${repeatIndex}`}>
                                {show.map((item) => (
                                    <div
                                        className="marquee-item"
                                        key={`${repeatIndex}-${item.id}`}
                                    >
                                        {item.icon && (
                                            <div className="alert">
                                                {getBannerIcon(item.icon)}
                                            </div>
                                        )}
                                        <div
                                            className="markdown-content"
                                            dangerouslySetInnerHTML={{
                                                __html: renderedMarkdownById[
                                                    item.id
                                                ],
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>
            </div>
            <button
                type="button"
                className="icon"
                aria-label="Dismiss banner"
                onClick={() => dismissAllBanners()}
            >
                <EonUiIcon
                    name="close"
                    style={{ height: 12, width: 12, flexShrink: 0 }}
                />
            </button>
        </div>
    );
};
